import { lazy, type ComponentType } from 'react';
import type { ViewRegistry } from '@/app/routing/view-registry';
import { useNavigation } from '@/app/navigation/use-navigation';

const BancoManager = lazy(() => import('@/features/financeiro/components/config/BancoManager'));
const ContaManager = lazy(() => import('@/features/financeiro/components/config/ContaManager'));
const CentroCustoManager = lazy(
  () => import('@/features/financeiro/components/config/CentroCustoManager'),
);
const CategoriaContabilManager = lazy(
  () => import('@/features/financeiro/components/config/CategoriaContabilManager'),
);
const FormaPagamento = lazy(() => import('@/features/financeiro/components/config/FormaPagamento'));
const ContasReceber = lazy(() => import('@/features/financeiro/components/ContasReceber'));
const ContasPagar = lazy(() => import('@/features/financeiro/components/ContasPagar'));
const Movimento = lazy(() => import('@/features/financeiro/components/Movimento'));
const Caixa = lazy(() => import('@/features/financeiro/components/Caixa'));
const Comissao = lazy(() => import('@/features/financeiro/components/Comissao'));
const ComissaoParametro = lazy(() => import('@/features/financeiro/components/ComissaoParametro'));
const ComissaoBonus = lazy(() => import('@/features/financeiro/components/ComissaoBonus'));
const Vendas = lazy(() => import('@/features/comercial/components/Vendas'));
const ResultadoProposta = lazy(() => import('@/features/comercial/components/ResultadoProposta'));
const BancoDetails = lazy(() => import('@/features/financeiro/components/config/BancoDetails'));
const ContaDetails = lazy(() => import('@/features/financeiro/components/config/ContaDetails'));
const CentroCustoDetails = lazy(() => import('@/features/financeiro/components/config/CentroCustoDetails'));

function makeOnBackOnly(Component: ComponentType<{ onBack: () => void }>) {
  return function View() {
    const { actions } = useNavigation();
    return <Component onBack={actions.goBack} />;
  };
}

function BancosView() {
  const { actions } = useNavigation();
  return (
    <BancoManager
      onBack={actions.goBack}
      onRowClick={(banco) =>
        actions.navigateWithSelection('banco-details', { bancoId: banco.id })
      }
    />
  );
}

function ContasView() {
  const { actions, viewParams } = useNavigation();
  return (
    <ContaManager
      onBack={actions.goBack}
      contaParaEditar={viewParams?.initialOpenRecordId}
      onContaParaEditarConsumida={() =>
        actions.patchViewParams({ initialOpenRecordId: undefined })
      }
      onRowClick={(conta) =>
        actions.navigateWithSelection('conta-details', { contaId: conta.id })
      }
    />
  );
}

function CentrosCustoView() {
  const { actions } = useNavigation();
  return (
    <CentroCustoManager
      onBack={actions.goBack}
      onRowClick={(centro) =>
        actions.navigateWithSelection('centro-custo-details', { centroCustoId: centro.id })
      }
    />
  );
}

function CategoriasContabeisView() {
  const { actions } = useNavigation();
  return <CategoriaContabilManager onBack={actions.goBack} onRowClick={undefined} />;
}

function BancoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <BancoDetails
      bancoId={selection.bancoId ?? ''}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={() => actions.navigate('bancos')}
    />
  );
}

function ContaDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <ContaDetails
      contaId={selection.contaId ?? ''}
      onBack={actions.goBack}
      onEdit={(conta) =>
        actions.navigateWithSelection(
          'contas-bancarias',
          { contaId: conta.id },
          { initialOpenRecordId: conta.id },
        )
      }
      onDelete={() => actions.navigate('contas-bancarias')}
    />
  );
}

function CentroCustoDetailsView() {
  const { selection, actions } = useNavigation();
  return (
    <CentroCustoDetails
      centroId={selection.centroCustoId ?? ''}
      onBack={actions.goBack}
      onEdit={() => undefined}
      onDelete={() => actions.navigate('centros-custo')}
    />
  );
}

export const financeiroViews: Partial<ViewRegistry> = {
  bancos: { component: BancosView, guard: { screen: 'bancos' } },
  'contas-bancarias': { component: ContasView, guard: { screen: 'contas-bancarias' } },
  'centros-custo': { component: CentrosCustoView, guard: { screen: 'centros-custo' } },
  'categorias-contabeis': {
    component: CategoriasContabeisView,
    guard: { screen: 'categorias-contabeis' },
  },
  'formas-pagamento': {
    component: makeOnBackOnly(FormaPagamento),
    guard: { permission: 'FORMA_PAGAMENTO' },
  },
  'contas-receber': {
    component: makeOnBackOnly(ContasReceber),
    guard: { screen: 'contas-receber' },
  },
  'contas-pagar': { component: makeOnBackOnly(ContasPagar), guard: { screen: 'contas-pagar' } },
  movimentos: { component: makeOnBackOnly(Movimento), guard: { screen: 'movimentos' } },
  caixa: { component: makeOnBackOnly(Caixa), guard: { screen: 'caixa' } },
  comissao: { component: makeOnBackOnly(Comissao), guard: { screen: 'comissao' } },
  'comissao-parametro': {
    component: makeOnBackOnly(ComissaoParametro),
    guard: { screen: 'comissao-parametro' },
  },
  'comissao-bonus': {
    component: makeOnBackOnly(ComissaoBonus),
    guard: { screen: 'comissao-bonus' },
  },
  vendas: { component: makeOnBackOnly(Vendas), guard: { screen: 'vendas' } },
  'proposta-resultado': {
    component: makeOnBackOnly(ResultadoProposta),
    guard: { permission: 'PROPOSTA_RESULTADO' },
  },
  'banco-details': { component: BancoDetailsView, guard: { screen: 'bancos' } },
  'conta-details': { component: ContaDetailsView, guard: { screen: 'contas-bancarias' } },
  'centro-custo-details': {
    component: CentroCustoDetailsView,
    guard: { screen: 'centros-custo' },
  },
};
